import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Settings, Save, Loader2, IndianRupee, Calendar, Users, 
  Target, TrendingUp, AlertCircle, Archive, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiUrl';

const IncentiveConfigPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [config, setConfig] = useState({
    regular_meeting_incentive: 100,
    dormant_meeting_incentive: 100,
    dormant_activation_incentive: 200,
    activation_window_days: 30,
    activation_min_brokerage_multiplier: 2.0,
    legacy_meeting_incentive: 0,
    brokerage_incentive_rate: 5.0
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/settings/incentives`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConfig({
          regular_meeting_incentive: data.regular_meeting_incentive || 100,
          dormant_meeting_incentive: data.dormant_meeting_incentive || 100,
          dormant_activation_incentive: data.dormant_activation_incentive || 200,
          activation_window_days: data.activation_window_days || 30,
          activation_min_brokerage_multiplier: data.activation_min_brokerage_multiplier || 2.0,
          legacy_meeting_incentive: data.legacy_meeting_incentive || 0,
          brokerage_incentive_rate: data.brokerage_incentive_rate || 5.0
        });
      }
    } catch (err) {
      console.error('Failed to fetch config:', err);
      toast.error('Failed to load incentive configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/settings/incentives`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          regular_meeting_incentive: parseInt(config.regular_meeting_incentive) || 0,
          dormant_meeting_incentive: parseInt(config.dormant_meeting_incentive) || 0,
          dormant_activation_incentive: parseInt(config.dormant_activation_incentive) || 0,
          activation_window_days: parseInt(config.activation_window_days) || 30,
          activation_min_brokerage_multiplier: parseFloat(config.activation_min_brokerage_multiplier) || 2.0,
          legacy_meeting_incentive: parseInt(config.legacy_meeting_incentive) || 0,
          brokerage_incentive_rate: parseFloat(config.brokerage_incentive_rate) || 5.0
        })
      });

      if (res.ok) {
        toast.success('Incentive configuration saved successfully');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save configuration');
      }
    } catch (err) {
      toast.error('Error saving configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReprocessActivations = async () => {
    if (!window.confirm('This will check all pending dormant activations against existing brokerage data. Continue?')) {
      return;
    }
    
    setReprocessing(true);
    try {
      const res = await fetch(`${API_URL}/incentives/reprocess-pending-activations`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Reprocessed ${data.summary.total_pending} pending activations. ${data.summary.activations_triggered} triggered!`);
        if (data.summary.below_threshold > 0) {
          toast.info(`${data.summary.below_threshold} activations below minimum brokerage threshold`);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to reprocess activations');
      }
    } catch (err) {
      console.error('Reprocess error:', err);
      toast.error('Error reprocessing activations');
    } finally {
      setReprocessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="incentive-config-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Settings className="w-6 h-6 text-[#C5A059]" />
            Incentive Configuration
          </h1>
          <p className="text-slate-400 mt-1">Configure all incentive amounts in one place</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-[#C5A059] hover:bg-[#b4914e]"
          data-testid="save-config-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Configuration
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
            <div>
              <p className="text-sm text-blue-300 font-medium">Incentive Rules</p>
              <ul className="text-xs text-blue-200/80 mt-1 space-y-1">
                <li>• <strong>Regular Meeting:</strong> Applies to meetings with Active or Dormant 1 clients</li>
                <li>• <strong>Dormant Meeting:</strong> Applies to meetings with Dormant 2 (26-59 days) or Dormant 3 (60+ days) clients</li>
                <li>• <strong>Dormant Activation:</strong> One-time bonus when brokerage is generated within the window after a dormant meeting</li>
                <li>• <strong>Brokerage Incentive:</strong> % of excess brokerage generated above monthly target</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Brokerage Incentive Rate - Prominent Position */}
      <Card className="card-dark border-emerald-500/30 bg-emerald-500/5">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            Brokerage Incentive Rate
          </CardTitle>
          <CardDescription>
            Percentage of excess brokerage above target that is paid as incentive
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="brokerage_rate">Incentive Rate (%)</Label>
              <Input
                id="brokerage_rate"
                type="number"
                step="0.1"
                value={config.brokerage_incentive_rate}
                onChange={(e) => setConfig({ ...config, brokerage_incentive_rate: e.target.value })}
                className="mt-1 bg-slate-800 border-white/10"
                data-testid="brokerage-rate-input"
              />
            </div>
            <div className="flex items-end">
              <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20 w-full">
                <p className="text-xs text-emerald-300">
                  <strong>Example:</strong> If target is ₹1,00,000 and actual is ₹1,50,000, 
                  excess is ₹50,000 × {config.brokerage_incentive_rate}% = ₹{(50000 * (parseFloat(config.brokerage_incentive_rate) || 5) / 100).toLocaleString('en-IN')} incentive
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Regular Meeting Incentive */}
        <Card className="card-dark border-white/5">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-400" />
              Regular Meeting Incentive
            </CardTitle>
            <CardDescription>
              Incentive for meetings with Active or Dormant 1 clients
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label htmlFor="regular_meeting">Amount per Meeting (₹)</Label>
                <div className="relative mt-1">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="regular_meeting"
                    type="number"
                    value={config.regular_meeting_incentive}
                    onChange={(e) => setConfig({ ...config, regular_meeting_incentive: e.target.value })}
                    className="pl-10 bg-slate-800 border-white/10"
                    data-testid="regular-meeting-input"
                  />
                </div>
              </div>
              <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                <p className="text-xs text-emerald-300">
                  <strong>Current:</strong> ₹{config.regular_meeting_incentive} per meeting with active clients
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dormant Meeting Incentive */}
        <Card className="card-dark border-white/5">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-amber-400" />
              Dormant Meeting Incentive
            </CardTitle>
            <CardDescription>
              Incentive for meetings with Dormant 2 or Dormant 3 clients
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label htmlFor="dormant_meeting">Amount per Meeting (₹)</Label>
                <div className="relative mt-1">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="dormant_meeting"
                    type="number"
                    value={config.dormant_meeting_incentive}
                    onChange={(e) => setConfig({ ...config, dormant_meeting_incentive: e.target.value })}
                    className="pl-10 bg-slate-800 border-white/10"
                    data-testid="dormant-meeting-input"
                  />
                </div>
              </div>
              <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                <p className="text-xs text-amber-300">
                  <strong>Current:</strong> ₹{config.dormant_meeting_incentive} per meeting with dormant clients
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dormant Activation Incentive */}
        <Card className="card-dark border-white/5">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#C5A059]" />
              Dormant Brokerage Activation Incentive
            </CardTitle>
            <CardDescription>
              One-time bonus for re-activating a dormant client (brokerage generated)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label htmlFor="activation_incentive">Activation Bonus (₹)</Label>
                <div className="relative mt-1">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="activation_incentive"
                    type="number"
                    value={config.dormant_activation_incentive}
                    onChange={(e) => setConfig({ ...config, dormant_activation_incentive: e.target.value })}
                    className="pl-10 bg-slate-800 border-white/10"
                    data-testid="activation-incentive-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="min_brokerage_multiplier">Minimum Brokerage Threshold (× Activation Bonus)</Label>
                <Input
                  id="min_brokerage_multiplier"
                  type="number"
                  step="0.5"
                  min="1"
                  value={config.activation_min_brokerage_multiplier}
                  onChange={(e) => setConfig({ ...config, activation_min_brokerage_multiplier: e.target.value })}
                  className="mt-1 bg-slate-800 border-white/10"
                  data-testid="min-brokerage-multiplier-input"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Client must generate at least ₹{(parseFloat(config.dormant_activation_incentive) * parseFloat(config.activation_min_brokerage_multiplier || 2)).toLocaleString('en-IN')} brokerage to trigger activation
                </p>
              </div>
              <div className="p-3 bg-[#C5A059]/10 rounded-lg border border-[#C5A059]/20">
                <p className="text-xs text-[#C5A059]">
                  <strong>Current:</strong> ₹{config.dormant_activation_incentive} bonus when brokerage ≥ ₹{(parseFloat(config.dormant_activation_incentive) * parseFloat(config.activation_min_brokerage_multiplier || 2)).toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Activation Window */}
        <Card className="card-dark border-white/5">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-sky-400" />
              Activation Window
            </CardTitle>
            <CardDescription>
              Days within which brokerage must be generated after dormant meeting
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label htmlFor="activation_window">Window (Days)</Label>
                <Input
                  id="activation_window"
                  type="number"
                  value={config.activation_window_days}
                  onChange={(e) => setConfig({ ...config, activation_window_days: e.target.value })}
                  className="mt-1 bg-slate-800 border-white/10"
                  data-testid="activation-window-input"
                />
              </div>
              <div className="p-3 bg-sky-500/10 rounded-lg border border-sky-500/20">
                <p className="text-xs text-sky-300">
                  <strong>Current:</strong> Brokerage must be recorded within {config.activation_window_days} days of meeting
                </p>
              </div>
              <div className="pt-4 border-t border-white/10">
                <Button
                  variant="outline"
                  onClick={handleReprocessActivations}
                  disabled={reprocessing}
                  className="w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                  data-testid="reprocess-activations-btn"
                >
                  {reprocessing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reprocessing...</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-2" /> Reprocess Pending Activations</>
                  )}
                </Button>
                <p className="text-xs text-slate-400 mt-2 text-center">
                  Check existing brokerage data against pending activations
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Legacy/Old Incentive Section */}
      <Card className="card-dark border-white/5 border-dashed">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Archive className="w-5 h-5 text-slate-400" />
            Legacy Incentive (Disabled)
          </CardTitle>
          <CardDescription>
            Old ₹250 per meeting incentive - Set to 0 by default. Keep for future reuse if needed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="legacy_incentive">Legacy Meeting Incentive (₹)</Label>
              <div className="relative mt-1">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="legacy_incentive"
                  type="number"
                  value={config.legacy_meeting_incentive}
                  onChange={(e) => setConfig({ ...config, legacy_meeting_incentive: e.target.value })}
                  className="pl-10 bg-slate-800 border-white/10"
                  data-testid="legacy-incentive-input"
                />
              </div>
            </div>
            <div className="flex items-end">
              <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-600/30 w-full">
                <p className="text-xs text-slate-400">
                  {config.legacy_meeting_incentive > 0 ? (
                    <span className="text-amber-400">⚠ Active: ₹{config.legacy_meeting_incentive} per meeting</span>
                  ) : (
                    <span>✓ Disabled (set to ₹0)</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Example Calculation */}
      <Card className="card-dark border-white/5">
        <CardHeader>
          <CardTitle className="text-white">Example Incentive Calculation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-300">Scenario: Meeting with Active Client</h4>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Regular Meeting Incentive</span>
                  <span className="text-emerald-400">₹{config.regular_meeting_incentive}</span>
                </div>
                <div className="border-t border-white/10 mt-2 pt-2 flex justify-between">
                  <span className="text-white font-medium">Total</span>
                  <span className="text-white font-bold">₹{config.regular_meeting_incentive}</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-300">Scenario: Meeting with Dormant Client + Activation</h4>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Dormant Meeting Incentive</span>
                  <span className="text-amber-400">₹{config.dormant_meeting_incentive}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-slate-400">Activation Bonus (if brokerage within {config.activation_window_days} days)</span>
                  <span className="text-[#C5A059]">₹{config.dormant_activation_incentive}</span>
                </div>
                <div className="border-t border-white/10 mt-2 pt-2 flex justify-between">
                  <span className="text-white font-medium">Maximum Total</span>
                  <span className="text-white font-bold">₹{parseInt(config.dormant_meeting_incentive || 0) + parseInt(config.dormant_activation_incentive || 0)}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IncentiveConfigPage;
